import { useState, useMemo, useEffect } from 'react';
import { MatchState, Team, Player, ValidationError } from '@/types';
import { getFirstServingTeam } from '@/lib/validation';
import AppHeader from '@/components/AppHeader';
import { getTournament } from '@/lib/supabase';

interface FirstServerSelectionProps {
  matchState: MatchState;
  teams: Team[];
  onSelectServer: (player: Player) => Promise<void>;
  onSignOut: () => void;
  readOnly?: boolean;
}

export default function FirstServerSelection({
  matchState,
  teams,
  onSelectServer,
  onSignOut,
  readOnly = false,
}: FirstServerSelectionProps) {
  const [error, setError] = useState<ValidationError | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string | null>(null);

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  const servingTeamId = useMemo(() => {
    if (!matchState.match) return null;
    return getFirstServingTeam(
      matchState.match.tossWonById,
      matchState.match.tossChoice,
      matchState.match.teamAId,
      matchState.match.teamBId
    );
  }, [matchState.match]);

  const servingTeam = useMemo(() => {
    return teams.find((t) => t.id === servingTeamId);
  }, [teams, servingTeamId]);

  const otherTeam = useMemo(() => {
    if (!matchState.match || !servingTeamId) return null;
    return teams.find(
      (t) => t.id === (servingTeamId === matchState.match!.teamAId ? matchState.match!.teamBId : matchState.match!.teamAId)
    );
  }, [teams, servingTeamId, matchState.match]);

  const handleSelectPlayer = async (player: Player) => {
    setSelecting(player.id);
    setError(null);
    try {
      await onSelectServer(player);
    } catch (err) {
      setError({
        id: 'server-error',
        title: 'INVALID FIRST SERVER',
        message: err instanceof Error ? err.message : 'Failed to select server',
      });
    } finally {
      setSelecting(null);
    }
  };

  const dismissError = () => setError(null);

  if (!servingTeam || !matchState.match) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  const matchTypeLabel = matchState.match.matchType.charAt(0).toUpperCase() + matchState.match.matchType.slice(1);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${matchState.match.matchNumber}`}
        subtitle={tournamentName ? `${matchTypeLabel} Match #${matchState.match.matchNumber}` : 'First Server Selection'}
        tournamentId={matchState.match.tournamentId ?? undefined}
        tournamentName={tournamentName ?? undefined}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-md mx-auto">
          {/* Instruction */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              SELECT FIRST SERVER
            </h1>
            <p className="text-text-secondary">
              <span className="font-semibold" style={{ color: servingTeam.color }}>
                {servingTeam.name}
              </span>{' '}
              serve first.
            </p>
            <p className="text-text-muted text-sm">
              Choose who opens the match.
            </p>
          </div>

          {/* Team Header */}
          <div
            className="rounded-lg p-3 mb-4"
            style={{ backgroundColor: `${servingTeam.color}15`, borderColor: servingTeam.color, borderWidth: 1 }}
          >
            <p className="font-semibold text-center" style={{ color: servingTeam.color }}>
              {servingTeam.name.toUpperCase()}
            </p>
          </div>

          {readOnly ? (
            <div className="card text-center py-8">
              <p className="text-text-muted text-sm">Waiting for server selection...</p>
              <p className="text-text-muted text-xs mt-1">Scorer will start this match shortly.</p>
            </div>
          ) : (
            /* Player Selection Grid */
            <div className="grid grid-cols-2 gap-3">
              {servingTeam.players.map((player) => {
                const isSelected = selecting === player.id;
                return (
                  <button
                    key={player.id}
                    onClick={() => handleSelectPlayer(player)}
                    disabled={selecting !== null}
                    className={`relative py-5 px-4 rounded-lg font-bold text-xl transition-all min-h-[72px] active:scale-[0.98] ${
                      isSelected
                        ? 'text-white'
                        : 'bg-surface-raised text-white'
                    }`}
                    style={
                      isSelected
                        ? { backgroundColor: servingTeam.color, border: `4px solid ${servingTeam.color}` }
                        : { border: `4px solid ${servingTeam.color}` }
                    }
                  >
                    {isSelected ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#ffffff', borderTopColor: 'transparent' }} />
                        {player.name}
                      </span>
                    ) : (
                      player.name
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* VS Separator */}
          {otherTeam && (
            <div className="flex items-center justify-center my-6">
              <div className="h-px bg-border flex-1" />
              <span className="text-text-muted px-4 text-sm">vs</span>
              <div className="h-px bg-border flex-1" />
            </div>
          )}

          {/* Other Team (non-serving, shown for context) */}
          {otherTeam && (
            <div className="opacity-50">
              <div className="rounded-lg p-3 mb-4 border border-border">
                <p className="font-semibold text-center text-text-secondary">
                  {otherTeam.name.toUpperCase()}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {otherTeam.players.map((player) => (
                  <div
                    key={player.id}
                    className="py-4 px-4 rounded-lg font-semibold text-lg bg-surface-raised border border-border text-text-muted cursor-not-allowed"
                  >
                    {player.name}
                  </div>
                ))}
              </div>
              <p className="text-text-muted text-xs text-center mt-2">
                Not serving this set
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Popup */}
      {error && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-surface border border-border rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-error text-3xl font-bold">X</span>
              </div>
              <h2 className="text-xl font-bold text-error mb-2">{error.title}</h2>
              <p className="text-text-secondary mb-6">{error.message}</p>
              <button
                onClick={dismissError}
                className="btn-primary bg-surface-raised border border-border text-text-primary"
              >
                UNDERSTOOD
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
